<template>
  <div>
    <page-header
      title="投后待办"
      :crumbs="['投后管理', '投后待办']"
      :subtitle="`${pendingCount} 项进行中 · 共 ${todoList.length} 项`"
    >
      <template #actions>
        <div class="view-switch">
          <button :class="{ 'is-active': view === 'list' }" @click="switchView('list')">列表</button>
          <button :class="{ 'is-active': view === 'board' }" @click="switchView('board')">看板</button>
        </div>
        <button class="btn btn-primary" @click="showCreateDialog = true">人工发起待办</button>
      </template>
    </page-header>

    <!-- 看板视图 -->
    <div v-if="view === 'board'" class="board">
      <div
        v-for="col in boardCols"
        :key="col.status"
        class="board__col"
        :class="{ 'is-over': dragOverCol === col.status }"
        @dragover.prevent="dragOverCol = col.status"
        @dragleave="dragOverCol = null"
        @drop="onDrop(col)"
      >
        <div class="board__head">
          <span class="board__dot" :style="{ background: col.color }"></span>
          <span class="board__title">{{ col.title }}</span>
          <span class="board__count">{{ todosByStatus(col.status).length }}</span>
        </div>

        <div
          v-for="todo in todosByStatus(col.status)"
          :key="todo.id"
          class="task"
          :class="[riskLevel(todo.riskType), { 'is-done': col.status === 2 || col.status === 3 }]"
          draggable="true"
          @dragstart="onDragStart(todo)"
          @dragend="onDragEnd"
          @click="viewTodo(todo)"
        >
          <div class="task__title">
            {{ todo.brandName || '—' }} · {{ riskTypeMap[todo.riskType] || '其他' }}
          </div>
          <div class="task__sub num">{{ todo.agreementNo || '—' }}</div>
          <div class="task__meta">
            <span class="tag tag-info">{{ todo.todoType || '待办' }}</span>
            <span class="task__time num">{{ shortTime(todo.createTime) }}</span>
          </div>
        </div>

        <p v-if="!todosByStatus(col.status).length" class="board__empty">暂无</p>
      </div>
    </div>

    <!-- 列表视图 -->
    <DataTable
      v-else
      :columns="columns"
      :data="todoList"
      :total="0"
      row-key="id"
      :ops-width="160"
      empty-text="暂无待办"
      @refresh="fetchTodoList"
    >
      <template #toolbar-left>
        <span class="dt-hint">共 {{ todoList.length }} 项 · {{ pendingCount }} 项进行中</span>
      </template>

      <template #cell-riskType="{ row }">
        {{ riskTypeMap[row.riskType] || row.riskType || '—' }}
      </template>

      <template #cell-agreementStatus="{ row }">
        {{ agreementStatusMap[row.agreementStatus] || row.agreementStatus || '—' }}
      </template>

      <template #cell-status="{ row }">
        <span class="tag" :class="statusTag(row.status)">{{ statusMap[row.status] || row.status }}</span>
      </template>

      <template #ops="{ row }">
        <button class="dt-op" @click="viewTodo(row)">详情</button>
        <span class="dt-sep">·</span>
        <button class="dt-op" @click="openUpdateStatusDialog(row)">状态</button>
      </template>
    </DataTable>

    <!-- 人工发起待办 -->
    <el-dialog v-model="showCreateDialog" title="人工发起待办" width="560px" :close-on-click-modal="false">
      <el-form ref="createFormRef" :model="formData" :rules="formRules" label-position="top">
        <el-form-item label="品牌" prop="brandCode">
          <el-select v-model="formData.brandCode" placeholder="请选择品牌" filterable style="width: 100%" @change="onBrandChange">
            <el-option v-for="b in brandList" :key="b.brandCode" :label="b.brandName" :value="b.brandCode" />
          </el-select>
        </el-form-item>
        <el-form-item label="协议" prop="agreementNo">
          <el-select v-model="formData.agreementNo" placeholder="请选择协议" filterable :disabled="!formData.brandCode" style="width: 100%">
            <el-option v-for="a in agreementList" :key="a.agreementNo" :label="a.agreementNo" :value="a.agreementNo" />
          </el-select>
        </el-form-item>
        <div class="form-row">
          <el-form-item label="协议状态" prop="agreementStatus">
            <el-select v-model="formData.agreementStatus" placeholder="请选择" style="width: 100%">
              <el-option v-for="(v, k) in agreementStatusMap" :key="k" :label="v" :value="Number(k)" />
            </el-select>
          </el-form-item>
          <el-form-item label="风险类型" prop="riskType">
            <el-select v-model="formData.riskType" placeholder="请选择" style="width: 100%">
              <el-option v-for="(v, k) in riskTypeMap" :key="k" :label="v" :value="Number(k)" />
            </el-select>
          </el-form-item>
        </div>
        <el-form-item :label="timeLabel" prop="time">
          <el-date-picker v-model="formData.time" type="datetime" placeholder="请选择时间" value-format="YYYY-MM-DD HH:mm:ss" style="width: 100%" />
        </el-form-item>
        <el-form-item label="待办类型">
          <el-input v-model="formData.todoType" disabled />
        </el-form-item>
        <el-form-item label="待办详情" prop="detail">
          <el-input v-model="formData.detail" type="textarea" :rows="4" placeholder="请输入待办详情" />
        </el-form-item>
        <el-form-item label="附件">
          <el-upload action="/api/upload" :on-success="handleUploadSuccess" :on-error="handleUploadError" :file-list="fileList" :limit="1" accept=".doc,.docx,.pdf,.jpg,.jpeg,.png">
            <button type="button" class="btn">点击上传</button>
            <template #tip>
              <p class="form-hint">支持 .doc, .docx, .pdf, .jpg, .jpeg, .png</p>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="btn" @click="resetForm">取消</button>
        <button class="btn btn-primary" @click="submitForm">提交</button>
      </template>
    </el-dialog>

    <!-- 详情 -->
    <el-dialog v-model="showDetailDialog" title="待办详情" width="720px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="ID">{{ detailData.id || '—' }}</el-descriptions-item>
        <el-descriptions-item label="品牌">{{ detailData.brandName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="协议编号">{{ detailData.agreementNo || '—' }}</el-descriptions-item>
        <el-descriptions-item label="协议状态">{{ detailData.agreementStatusLabel || '—' }}</el-descriptions-item>
        <el-descriptions-item label="风险类型">{{ detailData.riskTypeLabel || '—' }}</el-descriptions-item>
        <el-descriptions-item label="待办类型">{{ detailData.todoType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <span class="tag" :class="statusTag(detailData.status)">{{ detailData.statusLabel || '—' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ detailData.createTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="创建人">{{ detailData.createBy || '—' }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ detailData.updateTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="待办详情" :span="2">{{ detailData.detail || '—' }}</el-descriptions-item>
        <el-descriptions-item label="附件" :span="2">
          <template v-if="detailData.attachments && detailData.attachments.length">
            <a
              v-for="(a, i) in detailData.attachments"
              :key="i"
              class="dt-link"
              :href="a"
              target="_blank"
              style="margin-right: 12px"
            >附件 {{ i + 1 }}</a>
          </template>
          <span v-else class="tertiary">无附件</span>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <button class="btn" @click="closeDetailDialog">关闭</button>
        <button class="btn btn-primary" @click="openUpdateStatusDialog(detailData)">修改状态</button>
      </template>
    </el-dialog>

    <!-- 状态修改 -->
    <el-dialog v-model="showUpdateStatusDialog" title="修改状态" width="420px" :close-on-click-modal="false">
      <el-form label-position="top">
        <el-form-item label="待办 ID">
          <el-input :model-value="updateStatusForm.id" disabled />
        </el-form-item>
        <el-form-item label="当前状态">
          <el-input :model-value="updateStatusForm.currentStatusLabel" disabled />
        </el-form-item>
        <el-form-item label="新状态">
          <el-select v-model="updateStatusForm.status" placeholder="请选择新状态" style="width: 100%">
            <el-option v-for="s in nextStatusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <button class="btn" @click="closeUpdateStatusDialog">取消</button>
        <button class="btn btn-primary" @click="updateStatus">确定</button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import assetService from '@/services/assetService'
import postInvestService from '@/services/postInvestService'
import store from '@/store'

const view = ref(localStorage.getItem('rbf_todo_view') || 'board')

const showCreateDialog = ref(false)
const createFormRef = ref(null)
const showDetailDialog = ref(false)
const showUpdateStatusDialog = ref(false)

const statusMap = { 0: '待处理', 1: '处理中', 2: '已完成', 3: '已撤销' }
const agreementStatusMap = { 1: '联营中', 2: '联营结束', 3: '投后风险处理', 4: '提前终止联营' }
const riskTypeMap = {
  1: '友好协商回购', 2: '亏损闭店', 3: '欠款',
  4: '数据未传输', 5: '违约闭店', 6: '隐匿收入', 7: '其他'
}

// 风险类型 → 卡片左侧色条：高 / 中 / 低
const HIGH_RISK = [3, 5, 6]
const MID_RISK = [2, 4]

const boardCols = [
  { status: 0, title: '待处理', color: 'var(--danger-fill)' },
  { status: 1, title: '处理中', color: 'var(--warning-fill)' },
  { status: 2, title: '已完成', color: 'var(--success-fill)' },
  { status: 3, title: '已撤销', color: 'var(--neutral-300)' }
]

const columns = [
  { prop: 'id', label: 'ID', width: 70 },
  { prop: 'brandName', label: '品牌', minWidth: 120 },
  { prop: 'agreementNo', label: '协议编号', minWidth: 150 },
  { prop: 'agreementStatus', label: '协议状态', minWidth: 130 },
  { prop: 'riskType', label: '风险类型', minWidth: 130 },
  { prop: 'todoType', label: '待办类型', minWidth: 110 },
  { prop: 'createTime', label: '创建时间', minWidth: 150 },
  { prop: 'status', label: '状态', width: 100 }
]

const updateStatusForm = reactive({ id: '', currentStatus: '', currentStatusLabel: '', status: '' })
const detailData = reactive({
  id: '', brandName: '', agreementNo: '', agreementStatus: '', agreementStatusLabel: '',
  riskType: '', riskTypeLabel: '', todoType: '', createTime: '', status: '', statusLabel: '',
  detail: '', attachments: [], createBy: '', updateBy: '', updateTime: ''
})

const brandList = ref([])
const agreementList = ref([])
const fileList = ref([])
const todoList = ref([])

const formData = reactive({
  brandCode: '', brandName: '', agreementNo: '', agreementStatus: '', riskType: '',
  time: '', todoType: '人工发起', detail: '', attachments: [], createBy: '', updateBy: ''
})

const formRules = {
  brandCode: [{ required: true, message: '请选择品牌', trigger: 'change' }],
  agreementNo: [{ required: true, message: '请选择协议', trigger: 'change' }],
  agreementStatus: [{ required: true, message: '请选择协议状态', trigger: 'change' }],
  riskType: [{ required: true, message: '请选择风险类型', trigger: 'change' }],
  time: [{ required: true, message: '请选择时间', trigger: 'change' }],
  detail: [{ required: true, message: '请输入待办详情', trigger: 'blur' }]
}

const timeLabel = computed(() => {
  if (formData.agreementStatus === 3) return '风险识别时间'
  if (formData.agreementStatus === 4) return '联营终止时间'
  return '时间'
})

const pendingCount = computed(
  () => todoList.value.filter(t => Number(t.status) === 0 || Number(t.status) === 1).length
)

const todosByStatus = (status) => todoList.value.filter(t => Number(t.status) === Number(status))

const statusTag = (status) => ({
  0: 'tag-warning', 1: 'tag-info', 2: 'tag-success', 3: 'tag-neutral'
}[Number(status)] || 'tag-neutral')

const riskLevel = (riskType) => {
  const v = Number(riskType)
  if (HIGH_RISK.includes(v)) return 'is-high'
  if (MID_RISK.includes(v)) return 'is-mid'
  return 'is-low'
}

const shortTime = (t) => (t ? String(t).slice(5, 16) : '—')

const nextStatusOptions = computed(() =>
  [1, 2, 3].map(v => ({ value: v, label: statusMap[v] }))
)

const fetchBrandList = async () => {
  try {
    const res = await assetService.getBrandList({})
    brandList.value = Array.isArray(res?.data) ? res.data : (res?.data?.list || [])
  } catch (e) {
    ElMessage.error(e?.message || '获取品牌列表失败')
  }
}

const fetchAgreementList = async (brandCode) => {
  try {
    const res = await assetService.getAgreementListByBrandCode({ brandCode })
    agreementList.value = Array.isArray(res?.data) ? res.data : (res?.data?.list || [])
  } catch (e) {
    ElMessage.error(e?.message || '获取协议列表失败')
  }
}

const onBrandChange = (brandCode) => {
  formData.agreementNo = ''
  agreementList.value = []
  if (!brandCode) return
  const brand = brandList.value.find(b => b.brandCode === brandCode)
  if (brand) formData.brandName = brand.brandName
  fetchAgreementList(brandCode)
}

const handleUploadSuccess = (response) => {
  if (response?.code === 0) {
    formData.attachments.push(response.data.fileUrl)
    ElMessage.success('文件上传成功')
  } else {
    ElMessage.error(response?.message || '文件上传失败')
  }
}

const handleUploadError = () => ElMessage.error('文件上传失败')

const resetForm = () => {
  createFormRef.value?.resetFields()
  fileList.value = []
  formData.attachments = []
  showCreateDialog.value = false
}

const submitForm = async () => {
  if (!createFormRef.value) return
  try {
    await createFormRef.value.validate()
    const uid = store.state.userInfo?.id || ''
    formData.createBy = uid
    formData.updateBy = uid
    await postInvestService.createTodo({ ...formData, tenantId: store.state.tenantId })
    ElMessage.success('待办创建成功')
    resetForm()
    fetchTodoList()
  } catch (e) {
    ElMessage.error(e?.message || '待办创建失败')
  }
}

const viewTodo = (todo) => {
  Object.assign(detailData, todo)
  detailData.statusLabel = statusMap[todo.status] || todo.status
  detailData.agreementStatusLabel = agreementStatusMap[todo.agreementStatus] || todo.agreementStatus
  detailData.riskTypeLabel = riskTypeMap[todo.riskType] || todo.riskType
  if (!Array.isArray(detailData.attachments)) {
    detailData.attachments = detailData.attachments ? [detailData.attachments] : []
  }
  showDetailDialog.value = true
}

const closeDetailDialog = () => {
  showDetailDialog.value = false
  Object.assign(detailData, {
    id: '', brandName: '', agreementNo: '', agreementStatus: '', agreementStatusLabel: '',
    riskType: '', riskTypeLabel: '', todoType: '', createTime: '', status: '', statusLabel: '',
    detail: '', attachments: [], createBy: '', updateBy: '', updateTime: ''
  })
}

const fetchTodoList = async () => {
  try {
    const res = await postInvestService.getTodoList({ tenantId: store.state.tenantId })
    todoList.value = Array.isArray(res?.data) ? res.data : (res?.data?.list || [])
  } catch (e) {
    ElMessage.error(e?.message || '获取待办列表失败')
  }
}

const openUpdateStatusDialog = (todo) => {
  updateStatusForm.id = todo.id
  updateStatusForm.currentStatus = todo.status
  updateStatusForm.currentStatusLabel = statusMap[todo.status] || todo.status
  updateStatusForm.status = ''
  showUpdateStatusDialog.value = true
}

const closeUpdateStatusDialog = () => {
  showUpdateStatusDialog.value = false
  Object.assign(updateStatusForm, { id: '', currentStatus: '', currentStatusLabel: '', status: '' })
}

const updateStatus = async () => {
  if (!updateStatusForm.status) {
    ElMessage.warning('请选择新状态')
    return
  }
  try {
    await postInvestService.updateTodoStatus({
      id: updateStatusForm.id,
      status: updateStatusForm.status,
      updateBy: store.state.userInfo?.id || ''
    })
    ElMessage.success('状态更新成功')
    closeUpdateStatusDialog()
    showDetailDialog.value = false
    fetchTodoList()
  } catch (e) {
    ElMessage.error(e?.message || '状态更新失败')
  }
}

/* ---------- 看板拖拽 ---------- */
const dragTodo = ref(null)
const dragOverCol = ref(null)

const onDragStart = (todo) => { dragTodo.value = todo }

const onDragEnd = () => {
  dragTodo.value = null
  dragOverCol.value = null
}

// 拖拽即改状态，无需打开表单
const onDrop = async (col) => {
  const todo = dragTodo.value
  dragOverCol.value = null
  if (!todo || Number(todo.status) === Number(col.status)) {
    dragTodo.value = null
    return
  }
  const target = Number(col.status)
  const prev = todo.status
  todo.status = target
  try {
    await postInvestService.updateTodoStatus({
      id: todo.id,
      status: target,
      updateBy: store.state.userInfo?.id || ''
    })
    ElMessage.success(`已移至「${col.title}」`)
  } catch (e) {
    todo.status = prev
    ElMessage.error(e?.message || '状态更新失败')
  } finally {
    dragTodo.value = null
  }
}

onMounted(async () => {
  await fetchBrandList()
  await fetchTodoList()
})

// 视图偏好持久化
const switchView = (v) => {
  view.value = v
  localStorage.setItem('rbf_todo_view', v)
}
</script>

<script>
export default { name: 'PostInvestTodo' }
</script>

<style scoped>
.view-switch {
  display: flex;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  overflow: hidden;
}

.view-switch button {
  padding: 5px 12px;
  border: none;
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}

.view-switch button.is-active {
  background: var(--brand-500);
  color: #fff;
}

/* ---------- 看板 ---------- */
.board {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.board__col {
  flex: 1;
  min-width: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 11px;
  min-height: 180px;
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);
}

.board__col.is-over {
  border-color: var(--brand-500);
  background: var(--bg-selected);
}

.board__head {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.board__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 7px;
}

.board__title {
  font-size: 13px;
  font-weight: 500;
}

.board__count {
  margin-left: auto;
  font-size: 11px;
  color: var(--text-secondary);
  background: var(--bg-page);
  padding: 1px 6px;
  border-radius: 8px;
}

.board__empty {
  margin: 12px 0;
  text-align: center;
  font-size: 12px;
  color: var(--text-disabled);
}

.task {
  border: 1px solid var(--border);
  border-left: 3px solid var(--neutral-200);
  border-radius: 8px;
  padding: 10px 11px;
  margin-bottom: 8px;
  background: var(--bg-card);
  cursor: grab;
  transition: box-shadow var(--dur-fast) var(--ease);
}

.task:hover {
  box-shadow: var(--shadow-md);
}

.task:active {
  cursor: grabbing;
}

.task.is-high { border-left-color: var(--danger-fill); }
.task.is-mid  { border-left-color: var(--warning-fill); }
.task.is-low  { border-left-color: var(--neutral-200); }

.task.is-done {
  opacity: .72;
}

.task.is-done .task__title {
  text-decoration: line-through;
  text-decoration-color: var(--text-disabled);
}

.task__title {
  font-size: 13px;
  color: var(--text-primary);
}

.task__sub {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.task__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.task__time {
  margin-left: auto;
  font-size: 10px;
  color: var(--text-tertiary);
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row > * {
  flex: 1;
  min-width: 0;
}

.form-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
}

.dt-hint {
  font-size: 12px;
  color: var(--text-secondary);
}

.dt-link {
  color: var(--brand-500);
  text-decoration: none;
}

.dt-link:hover {
  text-decoration: underline;
}

.tertiary {
  color: var(--text-tertiary);
}

@media (max-width: 1180px) {
  .board {
    flex-direction: column;
  }
  .board__col {
    width: 100%;
  }
}
</style>
